import * as THREE from 'three';

/**
 * surfaceTextures.ts — procedural, SEAMLESSLY-TILEABLE asphalt maps for the road
 * (M35 graphics overhaul). The road used to be one flat colour; this gives it a
 * fine grain that catches the sun + IBL so the tarmac reads as a real surface.
 *
 * Everything is generated once (memoised) and shared across all cities — the
 * per-city road TINT is applied via the material's `color`, so these maps stay
 * neutral grayscale (detail map) + a tangent-space normal map. Tileable because
 * the value noise samples a wrapping grid, so there are no repeat seams down a
 * long straight.
 */

let cache: { detail: THREE.CanvasTexture; normal: THREE.CanvasTexture } | null = null;

export function asphaltTextures(): { detail: THREE.CanvasTexture; normal: THREE.CanvasTexture } {
  if (cache) return cache;
  const S = 256;
  const height = buildHeight(S);

  // Detail (grayscale around mid): multiplies the per-city road colour, adding
  // speckle + faint light/dark patches without changing the base tint much.
  const detail = paint(S, (data) => {
    for (let i = 0; i < S * S; i++) {
      const v = clamp8(190 + (height[i] - 0.5) * 90);
      const o = i * 4;
      data[o] = data[o + 1] = data[o + 2] = v;
      data[o + 3] = 255;
    }
  });
  detail.colorSpace = THREE.SRGBColorSpace;
  configure(detail, 7, 1);

  // Normal map from the height field (finite-difference, wrapping).
  const strength = 2.4;
  const normal = paint(S, (data) => {
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const hl = height[y * S + ((x - 1 + S) % S)];
        const hr = height[y * S + ((x + 1) % S)];
        const hu = height[((y - 1 + S) % S) * S + x];
        const hd = height[((y + 1) % S) * S + x];
        const dx = (hl - hr) * strength;
        const dy = (hd - hu) * strength;
        const len = Math.hypot(dx, dy, 1);
        const o = (y * S + x) * 4;
        data[o] = clamp8(((dx / len) * 0.5 + 0.5) * 255);
        data[o + 1] = clamp8(((dy / len) * 0.5 + 0.5) * 255);
        data[o + 2] = clamp8(((1 / len) * 0.5 + 0.5) * 255);
        data[o + 3] = 255;
      }
    }
  });
  configure(normal, 14, 2); // finer grain than the detail map

  // These are shared singletons across all cities — flag them so the per-city
  // mesh disposer (Game.disposeObject) never frees them out from under us.
  detail.userData.shared = true;
  normal.userData.shared = true;

  cache = { detail, normal };
  return cache;
}

/** A few octaves of tileable value noise → a height field in [0,1]. */
function buildHeight(S: number): Float32Array {
  const h = new Float32Array(S * S);
  let amp = 0;
  for (const [grid, a, seed] of [
    [8, 0.5, 11],
    [16, 0.3, 23],
    [48, 0.2, 37],
  ] as [number, number, number][]) {
    addOctave(h, S, grid, a, seed);
    amp += a;
  }
  for (let i = 0; i < h.length; i++) h[i] /= amp;
  return h;
}

/** Add one octave of wrapping bilinear value noise. */
function addOctave(out: Float32Array, S: number, grid: number, amp: number, seed: number): void {
  const rnd = mulberry32(seed);
  const g = new Float32Array(grid * grid);
  for (let i = 0; i < g.length; i++) g[i] = rnd();
  const smooth = (t: number): number => t * t * (3 - 2 * t);
  for (let y = 0; y < S; y++) {
    const gy = (y / S) * grid;
    const j0 = Math.floor(gy) % grid;
    const j1 = (j0 + 1) % grid;
    const fy = smooth(gy - Math.floor(gy));
    for (let x = 0; x < S; x++) {
      const gx = (x / S) * grid;
      const i0 = Math.floor(gx) % grid;
      const i1 = (i0 + 1) % grid;
      const fx = smooth(gx - Math.floor(gx));
      const top = g[j0 * grid + i0] * (1 - fx) + g[j0 * grid + i1] * fx;
      const bot = g[j1 * grid + i0] * (1 - fx) + g[j1 * grid + i1] * fx;
      out[y * S + x] += (top * (1 - fy) + bot * fy) * amp;
    }
  }
}

function configure(t: THREE.CanvasTexture, repeatX: number, repeatY: number): void {
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeatX, repeatY);
  t.anisotropy = 8;
}

function paint(S: number, fill: (data: Uint8ClampedArray) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context for surface texture');
  const img = ctx.createImageData(S, S);
  fill(img.data);
  ctx.putImageData(img, 0, 0);
  return new THREE.CanvasTexture(c);
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** Small deterministic RNG (matches the engine's mulberry32 elsewhere). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
